import Information from 'components/Information';
import Link from 'components/Link';
import Spinner from 'components/Loaders/Spinner';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import {handleBackendPagination} from 'components/Table/TableComponents/TablePagination/utils';
import Table from 'components/Table/TableStatic';
import {FC, ReactNode} from 'react';
import {useBackend} from 'state/backend';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {jstDateTimeString} from 'utils/date';
import updateUrlParamsAndReload from 'utils/pagination';

import useObjectListRefresh from './services';
import {DownloadsBackendData, DownloadStatus} from './types';

const TableContent: FC<DownloadsBackendData['data']> = ({
    objectList,
    pagination,
}) => {
    const STATUS_LABEL: Record<DownloadStatus, ReactNode> = {
        done: t`Ready for download`,
        error: t`Error`,
        new: (
            <div className="flex items-center justify-start">
                <Spinner className="mr-2" size="xs" />
                {t`Loading...`}
            </div>
        ),
    };

    return (
        <>
            <Table className="mt-4">
                <Table.Header>
                    <Table.HeaderRow>
                        <Table.Column>{t`File`}</Table.Column>
                        <Table.Column>{t`Status`}</Table.Column>
                        <Table.Column>{t`Date`}</Table.Column>
                        <Table.Column>{t`Requested By`}</Table.Column>
                        <Table.Column>{t`Memo`}</Table.Column>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {objectList.map((item) =>
                        item === null ? (
                            <Table.Row
                                className="bg-grey-100"
                                key={Math.random()}
                            >
                                <Table.Cell colSpan={5}>&nbsp;</Table.Cell>
                            </Table.Row>
                        ) : (
                            <Table.Row
                                className={twMerge(
                                    item.status !== 'done' &&
                                        'bg-grey-100 text-grey-600',
                                )}
                                key={item.mid}
                            >
                                <Table.Cell>
                                    {item.status === 'done' ? (
                                        <Link href={item.url}>
                                            {item.label}
                                        </Link>
                                    ) : (
                                        <div>{item.label}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {STATUS_LABEL[item.status]}
                                </Table.Cell>
                                <Table.Cell>
                                    {jstDateTimeString(new Date(item.datetime))}
                                </Table.Cell>
                                <Table.Cell>{item.requestedBy}</Table.Cell>
                                <Table.Cell className="max-w-48">
                                    {item.memo}
                                </Table.Cell>
                            </Table.Row>
                        ),
                    )}
                </Table.Body>
            </Table>
            <TablePagination
                onChange={(page) => updateUrlParamsAndReload('page', page)}
                onFirst={() => updateUrlParamsAndReload('page', 1)}
                onLast={() => updateUrlParamsAndReload('page', 'last')}
                {...handleBackendPagination(pagination)}
            />
        </>
    );
};

const EmptyContent = () => (
    <Information>{t`There are currently no files being prepared or available for download. Files are available for 24 hours after request.`}</Information>
);

const Downloads: FC = () => {
    const {appPermissions, data} = useBackend<DownloadsBackendData>();
    useObjectListRefresh(data.objectList, data.addingDownloads);
    return (
        <div className="flex flex-col gap-2">
            <NavigationTabs.History
                permissions={appPermissions}
                selection="downloads"
            />
            {data.objectList.length === 0 && !data.addingDownloads && (
                <EmptyContent />
            )}
            {data.addingDownloads === true && (
                <Information>
                    {t`We are analyzing your export request and will begin preparing your download shortly. Feel free to continue using the app in the meantime.`}
                    <span className="inline-block">
                        <Spinner className="mr-2" size="xs" />
                    </span>
                </Information>
            )}

            {data.objectList.length > 0 && (
                <>
                    {data.addingDownloads !== true &&
                        data.objectList.some(
                            (item) => item.status === 'new',
                        ) && (
                            <Information>
                                {t`Your download is being prepared. Feel free to continue using the app in the meantime.`}
                            </Information>
                        )}
                    <TableContent {...data} />
                </>
            )}
        </div>
    );
};
export default Downloads;
